import React, { useState } from "react";
import { Alert, Box, Button, Chip, Grid, MenuItem, Stack, TableCell, TableRow, TextField, Typography } from "@mui/material";
import { Add as AddIcon } from "@mui/icons-material";
import { useDispatch } from "react-redux";
import TableCommon from "../../components/Common/Table";
import ActionMenu, { ActionTypes } from "../../components/Common/ActionMenu";
import ConfirmationDialog from "../../components/Common/ConfirmationDialog";
import CouponDrawer from "../../components/admin/coupons/CouponDrawer";
import { setSnackBar } from "../../store/common/commonSlice";
import { useDeleteCouponMutation, useGetCouponsQuery } from "../../store/shopAdmin/shopAdminApis";
import { handleDateTimeDefault } from "../../helpers/utils";

const STATE = {
    live: { label: "Live", color: "success" },
    scheduled: { label: "Scheduled", color: "info" },
    expired: { label: "Expired", color: "default" },
    used_up: { label: "Used up", color: "warning" },
    inactive: { label: "Inactive", color: "default" },
};

const describe = (c) => {
    const amount = c.discount_type === "percent" ? `${Number(c.discount_value)}% off` : `₹${Number(c.discount_value)} off`;
    const cap = c.discount_type === "percent" && c.max_discount ? ` (max ₹${Number(c.max_discount)})` : "";
    const min = Number(c.min_cart_amount) > 0 ? ` on ₹${Number(c.min_cart_amount)}+` : "";
    return `${amount}${cap}${min}${c.first_order_only ? " · first order only" : ""}`;
};

const columns = [
    { title: "Code", field: "code", sort: false, width: "16%" },
    { title: "Offer", field: "offer", sort: false, width: "30%" },
    { title: "Used", field: "used_count", sort: false, width: "12%", align: "center" },
    { title: "Valid", field: "valid", sort: false, width: "20%" },
    { title: "Status", field: "state", sort: false, width: "12%", align: "center" },
    { title: "Actions", field: "actions", sort: false, width: "8%", align: "center" },
];

const CouponsView = () => {
    const dispatch = useDispatch();
    const [keywords, setKeywords] = useState("");
    const [status, setStatus] = useState("all");
    const [drawerCoupon, setDrawerCoupon] = useState(null);
    const [toDelete, setToDelete] = useState(null);
    const { data = [], isFetching, error } = useGetCouponsQuery({ keywords, status });
    const [deleteCoupon] = useDeleteCouponMutation();

    const handleDelete = async () => {
        try {
            await deleteCoupon(toDelete.id).unwrap();
            dispatch(setSnackBar({ open: true, message: "Coupon deleted", severity: "success" }));
        } catch (e) {
            dispatch(setSnackBar({ open: true, message: e?.data?.message || "Could not delete coupon", severity: "error" }));
        } finally {
            setToDelete(null);
        }
    };

    const rows = !isFetching && data.length
        ? data.map((c) => (
              <TableRow key={c.id} hover sx={{ "td, th": { border: 0, padding: "14px 12px" } }}>
                  <TableCell>
                      <Typography variant="subtitle2" fontWeight="bold" sx={{ letterSpacing: 1 }}>
                          {c.code}
                      </Typography>
                      {c.description ? (
                          <Typography variant="caption" color="text.secondary">
                              {c.description}
                          </Typography>
                      ) : null}
                  </TableCell>
                  <TableCell>{describe(c)}</TableCell>
                  <TableCell align="center">
                      {c.used_count}
                      {c.usage_limit ? ` / ${c.usage_limit}` : ""}
                  </TableCell>
                  <TableCell>
                      <Typography variant="caption" display="block">
                          {c.starts_at ? <>From {handleDateTimeDefault(c.starts_at, "d MMM yyyy")}</> : "From now"}
                      </Typography>
                      <Typography variant="caption" display="block">
                          {c.expires_at ? <>Until {handleDateTimeDefault(c.expires_at, "d MMM yyyy")}</> : "No expiry"}
                      </Typography>
                  </TableCell>
                  <TableCell align="center">
                      <Chip size="small" label={STATE[c.state]?.label || c.state} color={STATE[c.state]?.color || "default"} />
                  </TableCell>
                  <TableCell align="center">
                      <ActionMenu
                          actions={[
                              { ...ActionTypes.EDIT, onClick: () => setDrawerCoupon(c) },
                              { ...ActionTypes.DELETE, onClick: () => setToDelete(c) },
                          ].filter((a) => a.label)}
                          row={c}
                          showStatusToggle={false}
                      />
                  </TableCell>
              </TableRow>
          ))
        : null;

    return (
        <Stack spacing={2}>
            <Grid container justifyContent="space-between" alignItems="center">
                <Grid item>
                    <Typography variant="h6">
                        Coupons
                        <span style={{ color: "gray", fontSize: "10px", verticalAlign: "super", marginLeft: 8 }}>({data.length})</span>
                    </Typography>
                </Grid>
                <Grid item>
                    <Stack direction="row" spacing={1} alignItems="center">
                        <TextField select size="small" label="Status" value={status} onChange={(e) => setStatus(e.target.value)} sx={{ minWidth: 130 }}>
                            <MenuItem value="all">All</MenuItem>
                            <MenuItem value="active">Active</MenuItem>
                            <MenuItem value="inactive">Inactive</MenuItem>
                        </TextField>
                        <TextField size="small" placeholder="Search code" value={keywords} onChange={(e) => setKeywords(e.target.value)} />
                        <Button variant="contained" startIcon={<AddIcon />} onClick={() => setDrawerCoupon({})}>
                            New Coupon
                        </Button>
                    </Stack>
                </Grid>
            </Grid>
            <Alert severity="info">
                Customers enter these codes at checkout. One coupon per order. If a customer also qualifies for the
                first-order offer, the bigger discount applies. Changes affect new orders only.
            </Alert>
            {error && <Alert severity="error">{error?.data?.message || "Failed to load coupons"}</Alert>}
            <Box>
                <TableCommon
                    columns={columns}
                    tableData={rows}
                    count={data.length}
                    loading={isFetching}
                    pagination={{ pageNo: 0, limit: 100, sortBy: "DESC", sortField: "created_at", keywords: "" }}
                    handlePagination={() => {}}
                    emptyMessage="No coupons yet. Create one to offer a discount."
                />
            </Box>
            <CouponDrawer open={Boolean(drawerCoupon)} coupon={drawerCoupon} onClose={() => setDrawerCoupon(null)} />
            <ConfirmationDialog
                open={Boolean(toDelete)}
                onClose={() => setToDelete(null)}
                onConfirm={handleDelete}
                title={`Delete coupon ${toDelete?.code}?`}
                subtitle="Customers can no longer use it. Past orders keep their discount."
                confirmButtonTitle="Yes, Delete"
            />
        </Stack>
    );
};

export default CouponsView;
